/**
 * domain-shared.ts — felles innhold for de fire domenesidene (norsk + engelsk)
 *
 * UI-tekster, domenesøk, etiketter for filter, fordeler («Hvorfor inDomain») og footer.
 *
 * ⚠ PLASSHOLDERE: fordeler, kontaktadresse og alt som gjelder registrar-forhold må
 * bekreftes før publisering. Å selge domener krever avtale med register/registrar; for
 * .no må registrar eller forhandler være godkjent hos Norid, og .no har krav til
 * tilknytning til Norge. Søket validerer navnet og sender en forespørsel på e-post — det
 * sjekker IKKE tilgjengelighet (ingen backend finnes ennå).
 */

import type { Locale } from '@/types/landing';
import type { DomainSharedContent } from '@/types/domain';

/** TODO: bekreft riktig kontaktadresse (admin@ntune.no er den eneste i koden i dag). */
export const DOMAIN_CONTACT = 'mailto:admin@ntune.no?subject=inDomain';

const no: DomainSharedContent = {
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  currency: 'kr',
  search: {
    label: 'Søk etter domenenavn',
    placeholder: 'dittnavn.no',
    button: 'Søk',
    hint: 'Skriv inn navnet du ønsker, med eller uten domeneendelse.',
    errors: {
      empty: 'Skriv inn et domenenavn.',
      invalid: 'Domenenavnet kan bare inneholde bokstaver, tall og bindestrek, og kan ikke starte eller slutte med bindestrek.',
      tooLong: 'Domenenavnet er for langt (maks 63 tegn før endelsen).',
      unsupportedTld: 'Vi støtter ikke den endelsen ennå. Se «Fullstendig TLD-liste» for støttede endelser.',
    },
    resultsHeading: 'Domener for «{name}»',
    notice: 'Vi sjekker ikke tilgjengelighet automatisk ennå. Ta kontakt, så sjekker vi domenet og registrerer det for deg.',
    contactLabel: 'Be om registrering',
    contactHref: 'mailto:admin@ntune.no?subject=Domene%20{domain}',
    priceFrom: 'Fra',
    chipsLabel: 'Priser per domeneendelse (første år)',
  },
  labels: {
    all: 'Alle',
    popular: 'Mest populære',
    types: { country: 'Landsdomene', generic: 'Generisk', new: 'Nyere endelse' },
    categories: { popular: 'Populære', business: 'Næring', tech: 'Teknologi', creative: 'Kreativ' },
  },
  why: {
    heading: 'Hvorfor inDomain',
    items: [
      { icon: 'security', title: 'Personvern', body: 'Personopplysningene dine kan skjules i den offentlige WHOIS-oppføringen.' },
      { icon: 'support', title: 'Hjelp når du trenger det', body: 'Vi hjelper deg med registrering, flytting og oppsett.' },
      { icon: 'process', title: 'Enkelt oppsett', body: 'Pek domenet mot nettsted, e-post eller server uten å bruke tid på detaljene.' },
      { icon: 'globe', title: 'Alt på ett sted', body: 'Domene og hosting hos samme leverandør, med én innlogging.' },
    ],
  },
  footer: {
    tagline: 'inDomain — domenenavn med DNS og personvern, fra inSuite.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inDomain',
  },
};

const en: DomainSharedContent = {
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  currency: 'NOK',
  search: {
    label: 'Search for a domain name',
    placeholder: 'yourname.no',
    button: 'Search',
    hint: 'Enter the name you want, with or without a domain extension.',
    errors: {
      empty: 'Enter a domain name.',
      invalid: 'A domain name can only contain letters, numbers and hyphens, and cannot start or end with a hyphen.',
      tooLong: 'The domain name is too long (at most 63 characters before the extension).',
      unsupportedTld: 'We do not support that extension yet. See the “Full TLD list” for supported extensions.',
    },
    resultsHeading: 'Domains for “{name}”',
    notice: 'We do not check availability automatically yet. Get in touch, and we will check the domain and register it for you.',
    contactLabel: 'Request registration',
    contactHref: 'mailto:admin@ntune.no?subject=Domain%20{domain}',
    priceFrom: 'From',
    chipsLabel: 'Prices per domain extension (first year)',
  },
  labels: {
    all: 'All',
    popular: 'Most popular',
    types: { country: 'Country code', generic: 'Generic', new: 'New extension' },
    categories: { popular: 'Popular', business: 'Business', tech: 'Technology', creative: 'Creative' },
  },
  why: {
    heading: 'Why inDomain',
    items: [
      { icon: 'security', title: 'Privacy', body: 'Your personal details can be hidden in the public WHOIS record.' },
      { icon: 'support', title: 'Help when you need it', body: 'We help you with registration, transfers and setup.' },
      { icon: 'process', title: 'Simple setup', body: 'Point the domain to a website, email or a server without spending time on the details.' },
      { icon: 'globe', title: 'Everything in one place', body: 'Domain and hosting with the same provider, under one login.' },
    ],
  },
  footer: {
    tagline: 'inDomain — domain names with DNS and privacy, from inSuite.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inDomain',
  },
};

export const DOMAIN_SHARED: Record<Locale, DomainSharedContent> = { no, en };
